/**
 * Servicing activity list (business pages slice 3).
 * Newest first; filters by Subject and Activity Type (admin-editable picklists) plus a search box that
 * is ANDed with the organization scope. Call Plan and Results are shown side by side. Legacy
 * `description` text is shown under a neutral "Notes" label, never as a plan or a result.
 * Used by the organization Interactions tab (organizationId set) and the global Interactions page.
 */

import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getInteractions,
  deleteInteraction,
} from "../../services/interactions.service";
import {
  getLookupValues,
  isRewardInteraction,
} from "../../services/interactionServicing.service";
import { QUERY_KEYS, DEFAULT_PAGE_SIZE } from "../../utils/constants";
import { formatDate } from "../../utils/formatters";
import { useDebounce } from "../../hooks/useDebounce";
import type {
  Interaction,
  InteractionFilters,
} from "../../types/interaction.types";
import type { UUID } from "../../types/common.types";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import { LOOKUP_QUERY_KEY } from "./InteractionForm";

interface InteractionsListProps {
  organizationId?: UUID;
  locationId?: UUID;
  onEdit?: (interaction: Interaction) => void;
  onView?: (interaction: Interaction) => void;
  onAdd?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  voucher_issued: "Voucher Issued",
  offer_link_sent: "Offer Link Sent",
  offer_link_copied: "Offer Link Shared",
  physical_distributed: "Reward Item Handed Out",
  physical_shipped: "Reward Item Shipped",
};

const InteractionsList: React.FC<InteractionsListProps> = ({
  organizationId,
  locationId,
  onEdit,
  onView,
  onAdd,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [activityTypeFilter, setActivityTypeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 400);

  const { data: subjects } = useQuery({
    queryKey: [LOOKUP_QUERY_KEY, "subjects"],
    queryFn: () => getLookupValues("subjects"),
  });
  const { data: activityTypes } = useQuery({
    queryKey: [LOOKUP_QUERY_KEY, "activity_types"],
    queryFn: () => getLookupValues("activity_types"),
  });
  const subjectLabel = useMemo(
    () => new Map((subjects || []).map((s) => [s.id, s.label])),
    [subjects],
  );
  const activityTypeLabel = useMemo(
    () => new Map((activityTypes || []).map((a) => [a.id, a.label])),
    [activityTypes],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [
      QUERY_KEYS.INTERACTIONS,
      "list",
      page,
      organizationId,
      locationId,
      subjectFilter,
      activityTypeFilter,
      debouncedSearch,
    ],
    queryFn: () => {
      const filters: InteractionFilters = {};
      if (organizationId) {
        filters.organization_id = organizationId;
      }
      if (locationId) {
        filters.location_id = locationId;
      }
      if (subjectFilter) {
        filters.subject_id = subjectFilter;
      }
      if (activityTypeFilter) {
        filters.activity_type_id = activityTypeFilter;
      }
      if (debouncedSearch.trim()) {
        filters.search = debouncedSearch.trim();
      }
      return getInteractions(
        {
          page,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: "interaction_date",
          sortOrder: "desc",
        },
        filters,
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInteraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTERACTIONS] });
      showToast("success", "Activity deleted");
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "The activity could not be deleted.";
      showToast("error", msg);
    },
  });

  const handleDelete = (interaction: Interaction) => {
    if (
      window.confirm(
        isRewardInteraction(interaction)
          ? "Delete this event reward record? The reward itself is not affected."
          : "Are you sure you want to delete this activity?",
      )
    ) {
      deleteMutation.mutate(interaction.id);
    }
  };

  const rows = data?.data || [];
  const filtersActive = !!(
    subjectFilter ||
    activityTypeFilter ||
    debouncedSearch
  );
  const selectClass =
    "block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500";

  return (
    <div className="space-y-4" data-testid="interactions-list">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search title, notes, plan or results..."
              className={selectClass}
              data-testid="interactions-search"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <select
              value={subjectFilter}
              onChange={(e) => {
                setSubjectFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
              data-testid="interactions-filter-subject"
            >
              <option value="">All subjects</option>
              {(subjects || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                  {s.is_active ? "" : " (retired)"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Type
            </label>
            <select
              value={activityTypeFilter}
              onChange={(e) => {
                setActivityTypeFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
              data-testid="interactions-filter-activity-type"
            >
              <option value="">All activity types</option>
              {(activityTypes || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                  {a.is_active ? "" : " (retired)"}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" text="Loading activity..." />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              Error loading activity:{" "}
              {error instanceof Error ? error.message : "please try again."}
            </p>
          </div>
        ) : rows.length > 0 ? (
          <>
            <div
              className="text-sm text-gray-600 px-6 pt-4"
              data-testid="interactions-count"
            >
              {data?.total} {data?.total === 1 ? "activity" : "activities"}
            </div>
            <ul className="divide-y divide-gray-200">
              {rows.map((interaction) => {
                const reward = isRewardInteraction(interaction);
                const subject = interaction.subject_id
                  ? subjectLabel.get(interaction.subject_id)
                  : undefined;
                const activityType = interaction.activity_type_id
                  ? activityTypeLabel.get(interaction.activity_type_id)
                  : undefined;
                return (
                  <li
                    key={interaction.id}
                    className="px-6 py-4 hover:bg-gray-50"
                    data-testid="interaction-row"
                    data-interaction-id={interaction.id}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900">
                            {formatDate(interaction.interaction_date)}
                          </span>
                          {subject && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              {subject}
                            </span>
                          )}
                          {activityType && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                              {activityType}
                            </span>
                          )}
                          {reward && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              {TYPE_LABELS[interaction.type as string] ||
                                "Event reward"}
                            </span>
                          )}
                          {interaction.is_internal && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                              Internal
                            </span>
                          )}
                          {interaction.next_follow_up_date && (
                            <span className="text-xs text-gray-500">
                              Next task due{" "}
                              {formatDate(interaction.next_follow_up_date)}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-700">
                          {interaction.title}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-sm font-medium space-x-3">
                        {onView && (
                          <button
                            className="text-primary-600 hover:text-primary-900"
                            onClick={() => onView(interaction)}
                          >
                            View
                          </button>
                        )}
                        {onEdit && (
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => onEdit(interaction)}
                            data-testid="interaction-edit"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDelete(interaction)}
                          data-testid="interaction-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {(interaction.call_plan || interaction.results) && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-md bg-gray-50 p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Call Plan
                          </div>
                          <div
                            className="mt-1 text-sm text-gray-900 whitespace-pre-wrap"
                            data-testid="interaction-call-plan"
                          >
                            {interaction.call_plan || "—"}
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Results
                          </div>
                          <div
                            className="mt-1 text-sm text-gray-900 whitespace-pre-wrap"
                            data-testid="interaction-results"
                          >
                            {interaction.results || "—"}
                          </div>
                        </div>
                      </div>
                    )}
                    {interaction.description && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium text-gray-500">
                          Notes:
                        </span>{" "}
                        <span
                          className="whitespace-pre-wrap"
                          data-testid="interaction-notes"
                        >
                          {interaction.description}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {data && data.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {data.page} of {data.totalPages} ({data.total} total)
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page === data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12" data-testid="interactions-empty">
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {filtersActive
                ? "No activity matches these filters"
                : "No activity yet"}
            </h3>
            {!filtersActive && onAdd && (
              <div className="mt-4">
                <Button onClick={onAdd}>Add the first activity</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractionsList;
